import type { RfqProject, RfqSubmission } from '../types';
import { formatQuoteTotal, submissionQuoteTotal } from './rfqQuoteTotal';
import { groupQuoteSubmissionHistory } from './rfqSubmissionHistory';
import { resolveQuoteStreamDisplay } from './vendorPortalQuoteDisplay';
import { timberRfqDrynessSpecLabel, type SelectableTimberRfqDrynessSpec } from './supplierTimberExport';
import {
  inferQuotedSpeciesFromFileName,
  rfqProjectTimberSpeciesDrynessEntries,
  rfqProjectTimberSupplierDisplaySpecies,
  submissionQuotedDryness,
  submissionQuotedSpecies,
} from './timberRfqSuppliers';
import {
  timberQuoteStreamKey,
  timberQuoteStructureScopeLabel,
} from './timberQuoteStreamKey';

export type VendorPortalTimberQuoteHeading = {
  submission: RfqSubmission;
  species: string | null;
  drynessLabel: string;
  priceLabel: string;
  /** Structures this quote covers, or "Full scope". */
  structureScopeLabel: string;
  /** Superseded quotes for the same species + structure set, newest first. */
  previous: RfqSubmission[];
  /** Newer uploads not yet reviewed after an approved primary. */
  pendingRevisions: RfqSubmission[];
};

export function isActiveTimberQuoteSubmission(sub: RfqSubmission): boolean {
  if (sub.notBidding) return false;
  if (sub.storagePath || sub.cncQuote) return true;
  const total = submissionQuoteTotal(sub);
  return total != null && total > 0;
}

export function timberQuoteSubmissions(subs: RfqSubmission[]): RfqSubmission[] {
  return subs
    .filter(isActiveTimberQuoteSubmission)
    .sort((a, b) => {
      const aSpecies = a.quotedSpecies ?? a.fileName ?? '';
      const bSpecies = b.quotedSpecies ?? b.fileName ?? '';
      const byName = aSpecies.localeCompare(bSpecies, undefined, { sensitivity: 'base' });
      if (byName !== 0) return byName;
      const aTime = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
      const bTime = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
      return bTime - aTime;
    });
}

export function buildVendorPortalTimberQuoteHeadings(
  subs: RfqSubmission[],
  project: Pick<RfqProject, 'timberSupplierInvites' | 'timberDrynessSpec'>,
  orgId: string,
  approvedSubmissionIds: Set<string> = new Set(),
): VendorPortalTimberQuoteHeading[] {
  const active = timberQuoteSubmissions(subs);
  const speciesCatalog = rfqProjectTimberSupplierDisplaySpecies(project, orgId, active);
  const resolveSpecies = (sub: RfqSubmission) =>
    submissionQuotedSpecies(sub, speciesCatalog) ??
    inferQuotedSpeciesFromFileName(sub.fileName ?? '', speciesCatalog);

  return groupQuoteSubmissionHistory(active, sub =>
    timberQuoteStreamKey(sub, resolveSpecies),
  ).map(group => {
    const display = resolveQuoteStreamDisplay(group, approvedSubmissionIds);
    const sub = display.primary;
    const species = resolveSpecies(sub);
    const dryness = submissionQuotedDryness(sub, project, orgId, species ?? '');
    return {
      submission: sub,
      species,
      drynessLabel: timberRfqDrynessSpecLabel(dryness),
      priceLabel: formatQuoteTotal(submissionQuoteTotal(sub)),
      structureScopeLabel: timberQuoteStructureScopeLabel(sub),
      previous: display.previous,
      pendingRevisions: display.pendingRevisions,
    };
  });
}

export type VendorPortalTimberRequestedBadge = {
  species: string;
  drynessLabel: string;
};

export function buildVendorPortalTimberRequestedBadges(
  project: Pick<RfqProject, 'timberSupplierInvites' | 'timberDrynessSpec'>,
  orgId: string,
): VendorPortalTimberRequestedBadge[] {
  return rfqProjectTimberSpeciesDrynessEntries(project, orgId).map(entry => ({
    species: entry.species,
    drynessLabel: timberRfqDrynessSpecLabel(entry.drynessSpec),
  }));
}

export type VendorPortalTimberSpeciesQuoteOption = {
  species: string;
  drynessSpec: SelectableTimberRfqDrynessSpec;
  drynessLabel: string;
  /** True when any quote already exists for this species (another stream or revision still allowed). */
  alreadyQuoted: boolean;
};

/** Requested species for the quote submit modal; quoted ones stay selectable to allow revisions. */
export function buildTimberQuoteSubmitSpeciesOptions(
  project: Pick<RfqProject, 'timberSupplierInvites' | 'timberDrynessSpec'>,
  orgId: string,
  existingSubs: RfqSubmission[],
): VendorPortalTimberSpeciesQuoteOption[] {
  const entries = rfqProjectTimberSpeciesDrynessEntries(project, orgId);
  if (!entries.length) return [];

  const catalog = rfqProjectTimberSupplierDisplaySpecies(project, orgId, existingSubs);
  const quotedSpecies = new Set<string>();
  for (const sub of existingSubs) {
    if (!isActiveTimberQuoteSubmission(sub)) continue;
    const species = submissionQuotedSpecies(sub, catalog);
    if (species) quotedSpecies.add(species);
  }

  const unquotedFirst = [...entries].sort(
    (a, b) => Number(quotedSpecies.has(a.species)) - Number(quotedSpecies.has(b.species)),
  );
  return unquotedFirst.map(entry => ({
    species: entry.species,
    drynessSpec: entry.drynessSpec,
    drynessLabel: timberRfqDrynessSpecLabel(entry.drynessSpec),
    alreadyQuoted: quotedSpecies.has(entry.species),
  }));
}

export function supplierQuoteDownloadLabel(
  sub: Pick<RfqSubmission, 'fileName'>,
  species: string | null | undefined,
): string {
  if (species?.trim()) return species.trim();
  const name = sub.fileName?.trim();
  if (name && name !== 'Not bidding' && name !== 'CNC quote') return name;
  return 'Quote file';
}
